import { Badge, type TabsProps } from 'antd'
import { Icon } from '@iconify/react'
import { Checkout } from 'features/Procedure/features/checkout'
import { Labs } from 'features/Procedure/features/labs'
import { SignedDocuments } from 'features/Procedure/features/signed-documents'
import { Notes } from 'features/Procedure/features/notes'
import { Photos } from 'features/Procedure/features/photos'
import { Prescriptions } from 'features/Procedure/features/prescriptions'
import { Products } from 'features/Procedure/features/products'
import { Treatments } from 'features/Procedure/features/treatments'
import type { ReactNode } from 'react'
import type { ESignFormStatusData } from 'features/Procedure/state/procedure-api.type'
import {
  getTabHasESignItems,
  getTabSignedStatus,
  getTabHasFormToSign,
} from 'features/Procedure/components/e-sign-button/e-sign-status.utils'
import { Spinner } from 'common/components/Spinner'
import type { FormsNavigationRequest } from 'features/Procedure/features/signed-documents/signed-documents.types'
import styles from './tabs.config.module.scss'

type RegisterTabAction = (tabKey: string, action: ReactNode | null) => void
type RegisterTabLeaveAction = (tabKey: string, action: (() => Promise<void>) | null) => void

type TabLabelProps = {
  label: string
  isSigned: boolean
  hasSignHistory: boolean
  hasFormToSign: boolean
  isLoading?: boolean
}

const TabLabel = ({
  label,
  isSigned,
  hasSignHistory,
  hasFormToSign,
  isLoading = false,
}: TabLabelProps) => (
  <span className={styles.tabLabel}>
    {label}
    {isLoading ? (
      <span className={styles.statusIcon}>
        <Spinner isLoading size='small' />
      </span>
    ) : (
      hasFormToSign && (
        <span className={styles.statusIcon}>
          <Icon
            icon={isSigned ? 'uim:lock-alt' : 'uim:unlock-alt'}
            width={24}
            height={24}
            color={hasSignHistory ? 'var(--success)' : 'var(--warning)'}
          />
        </span>
      )
    )}
  </span>
)

export const getTabsConfig = (
  registerTabAction: RegisterTabAction,
  registerTabLeaveAction: RegisterTabLeaveAction | undefined,
  selectedPatientId: number | null,
  selectedEncounterUid: string | null,
  eSignStatus?: ESignFormStatusData,
  isESignStatusLoading = false,
  isInteractionLocked = false,
  onNavigateToForms?: (documentId?: number | null) => void,
  pendingFormsCount = 0,
  formsNavigationRequest?: FormsNavigationRequest | null,
): TabsProps['items'] => {
  const isEncounterSigned = eSignStatus?.encounter?.is_signed === 1

  return [
    {
      key: tabsKeys.treatments,
      label: (
        <TabLabel
          label='Treatments'
          isSigned={getTabSignedStatus(eSignStatus, tabsKeys.treatments)}
          hasSignHistory={getTabHasESignItems(eSignStatus, tabsKeys.treatments)}
          hasFormToSign={getTabHasFormToSign(eSignStatus, tabsKeys.treatments)}
          isLoading={isESignStatusLoading}
        />
      ),
      children: (
        <Treatments
          onRegisterAction={(node) => registerTabAction('treatments', node)}
          selectedPatientId={selectedPatientId}
          selectedEncounterUid={selectedEncounterUid}
          disableActions={
            isInteractionLocked || getTabSignedStatus(eSignStatus, tabsKeys.treatments)
          }
          onNavigateToForms={onNavigateToForms}
          onRegisterLeaveAction={(action) => registerTabLeaveAction?.(tabsKeys.treatments, action)}
        />
      ),
    },
    {
      key: tabsKeys.photos,
      label: (
        <TabLabel
          label='Photos'
          isSigned={getTabSignedStatus(eSignStatus, tabsKeys.photos)}
          hasSignHistory={getTabHasESignItems(eSignStatus, tabsKeys.photos)}
          hasFormToSign={getTabHasFormToSign(eSignStatus, tabsKeys.photos)}
          isLoading={isESignStatusLoading}
        />
      ),
      children: (
        <Photos
          selectedPatientId={selectedPatientId}
          selectedEncounterUid={selectedEncounterUid}
          disableActions={
            isInteractionLocked ||
            isEncounterSigned ||
            getTabSignedStatus(eSignStatus, tabsKeys.photos)
          }
          onRegisterLeaveAction={(action) => registerTabLeaveAction?.(tabsKeys.photos, action)}
        />
      ),
    },
    {
      key: tabsKeys.soap,
      label: (
        <TabLabel
          label='Notes'
          isSigned={getTabSignedStatus(eSignStatus, tabsKeys.soap)}
          hasSignHistory={getTabHasESignItems(eSignStatus, tabsKeys.soap)}
          hasFormToSign={getTabHasFormToSign(eSignStatus, tabsKeys.soap)}
          isLoading={isESignStatusLoading}
        />
      ),
      children: (
        <Notes
          selectedPatientId={selectedPatientId}
          selectedEncounterUid={selectedEncounterUid}
          disableActions={
            isInteractionLocked ||
            isEncounterSigned ||
            getTabSignedStatus(eSignStatus, tabsKeys.soap)
          }
          onRegisterLeaveAction={(action) => registerTabLeaveAction?.(tabsKeys.soap, action)}
        />
      ),
    },
    {
      key: tabsKeys.products,
      label: (
        <TabLabel
          label='Products'
          isSigned={getTabSignedStatus(eSignStatus, tabsKeys.products)}
          hasSignHistory={getTabHasESignItems(eSignStatus, tabsKeys.products)}
          hasFormToSign={getTabHasFormToSign(eSignStatus, tabsKeys.products)}
          isLoading={isESignStatusLoading}
        />
      ),
      children: (
        <Products
          onRegisterAction={(node) => registerTabAction('products', node)}
          selectedEncounterUid={selectedEncounterUid}
          disableActions={
            isInteractionLocked ||
            isEncounterSigned ||
            getTabSignedStatus(eSignStatus, tabsKeys.products)
          }
        />
      ),
    },
    {
      key: tabsKeys.prescriptions,
      label: 'Prescriptions',
      children: <Prescriptions selectedPatientId={selectedPatientId} />,
    },
    {
      key: tabsKeys.labs,
      label: (
        <TabLabel
          label='Labs'
          isSigned={getTabSignedStatus(eSignStatus, tabsKeys.labs)}
          hasSignHistory={getTabHasESignItems(eSignStatus, tabsKeys.labs)}
          hasFormToSign={getTabHasFormToSign(eSignStatus, tabsKeys.labs)}
          isLoading={isESignStatusLoading}
        />
      ),
      children: (
        <Labs
          selectedPatientId={selectedPatientId}
          selectedEncounterUid={selectedEncounterUid}
          disableActions={
            isInteractionLocked ||
            isEncounterSigned ||
            getTabSignedStatus(eSignStatus, tabsKeys.labs)
          }
        />
      ),
    },
    {
      key: tabsKeys.forms,
      label: (
        <>
          <Badge
            showZero
            offset={[10, 5]}
            color={pendingFormsCount > 0 ? 'var(--success)' : 'var(--danger)'}
            count={pendingFormsCount}
            size='small'>
            <span className={styles.tabLabelText}>Forms</span>
          </Badge>
          {/* do not touch it, it's needed for extra spacing for badge */}
          <div style={{ width: 60 }} />
        </>
      ),
      children: (
        <SignedDocuments
          selectedPatientId={selectedPatientId}
          navigationRequest={formsNavigationRequest}
          disableActions={isInteractionLocked}
        />
      ),
    },
    {
      key: tabsKeys.checkout,
      label: 'Checkout',
      children: (
        <Checkout
          selectedPatientId={selectedPatientId}
          selectedEncounterUid={selectedEncounterUid}
          disableActions={false}
        />
      ),
    },
  ]
}

export const tabsKeys = {
  treatments: 'treatments',
  photos: 'photos',
  soap: 'soap',
  products: 'products',
  prescriptions: 'prescriptions',
  labs: 'labs',
  forms: 'forms',
  checkout: 'checkout',
} as const
